import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Paper,
  Typography,
  Box,
  IconButton,
  Chip,
  Alert,
  TableContainer,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import request from '../../../api/request';
import { BackendAPI } from '../../../const/api';
import { useToast } from '../../../component/ToastBar';

interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string | null;
}

const ApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteKeyInfo, setDeleteKeyInfo] = useState<{id: string, name: string} | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [expiryDays, setExpiryDays] = useState<number | ''>('');
  const [createdKey, setCreatedKey] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await request.get(BackendAPI.apiKeys);
      if (response.success) {
        setApiKeys(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast('获取API Key列表失败', 'error');
    }
  }, [toast]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast('请输入API Key名称', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload: any = { name: newKeyName.trim() };
      if (expiryDays) {
        payload.expires_days = Number(expiryDays);
      }

      const response = await request.post(BackendAPI.apiKeys, payload);

      if (response.success) {
        setCreatedKey(response.data.apiKey);
        setNewKeyName('');
        setExpiryDays('');
        setShowCreateDialog(false);
        await fetchApiKeys();
        toast('API Key创建成功', 'success');
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast('创建API Key失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKey = async () => {
    if (!deleteKeyInfo) return;

    try {
      const response = await request.del(BackendAPI.deleteApiKey(deleteKeyInfo.id));
      if (response.success) {
        await fetchApiKeys();
        toast('API Key删除成功', 'success');
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast('删除API Key失败', 'error');
    } finally {
      setShowDeleteDialog(false);
      setDeleteKeyInfo(null);
    }
  };

  const openDeleteDialog = (keyId: string, keyName: string) => {
    setDeleteKeyInfo({ id: keyId, name: keyName });
    setShowDeleteDialog(true);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast('已复制到剪贴板', 'success');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast('复制失败', 'error');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">API Key</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateDialog(true)}
        >
          创建 API Key
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        API Key 可用于程序化访问系统API。请妥善保管您的API Key，不要在公开场所分享。
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>名称</TableCell>
              <TableCell>Key 前缀</TableCell>
              <TableCell>状态</TableCell>
              <TableCell>最后使用</TableCell>
              <TableCell>过期时间</TableCell>
              <TableCell>创建时间</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">暂无API Key</Typography>
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>
                        {key.keyPrefix}
                      </code>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isExpired(key.expiresAt) ? (
                      <Chip label="已过期" color="error" size="small" />
                    ) : (
                      <Chip label="活跃" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                  <TableCell>{formatDate(key.expiresAt)}</TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => openDeleteDialog(key.id, key.name)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 创建API Key对话框 */}
      <Dialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>创建 API Key</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <TextField
              label="名称"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              fullWidth
              margin="normal"
              placeholder="例如：生产环境API"
              helperText="为这个API Key取一个有意义的名称"
            />
            <TextField
              label="过期天数（可选）"
              type="number"
              value={expiryDays}
              onChange={(e) => setExpiryDays(e.target.value === '' ? '' : Number(e.target.value))}
              fullWidth
              margin="normal"
              placeholder="留空表示永不过期"
              helperText="设置API Key的有效期，留空表示永不过期"
              inputProps={{ min: 1, max: 3650 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>取消</Button>
          <Button 
            onClick={handleCreateKey} 
            variant="contained"
            disabled={loading || !newKeyName.trim()}
          >
            {loading ? '创建中...' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认对话框 */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>删除 API Key</DialogTitle>
        <DialogContent>
          <Typography>
            确定要删除API Key "{deleteKeyInfo?.name}" 吗？
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            此操作不可恢复，删除后该API Key将立即失效。
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>取消</Button>
          <Button 
            onClick={handleDeleteKey} 
            variant="contained"
            color="error"
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 显示创建的API Key */}
      <Dialog 
        open={!!createdKey} 
        onClose={() => setCreatedKey('')}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>API Key 创建成功</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            请立即复制并保存以下API Key。出于安全考虑，您将无法再次查看完整的API Key。
          </Alert>
          <Box 
            display="flex" 
            alignItems="center" 
            gap={1}
            p={2}
            border={1}
            borderColor="grey.300"
            borderRadius={1}
            bgcolor="grey.50"
          >
            <TextField
              value={createdKey}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
                style: { fontFamily: 'monospace' }
              }}
            />
            <IconButton
              onClick={() => copyToClipboard(createdKey)}
              color="primary"
            >
              <CopyIcon />
            </IconButton>
          </Box>
          <Typography variant="body2" color="textSecondary" mt={2}>
            使用方法：在HTTP请求头中添加 <code>X-API-Key: {createdKey}</code>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatedKey('')} variant="contained">
            我已保存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys; 